import {defineMessages} from 'react-intl'

/**
 * Catálogo compartido de los paneles de seguridad (2FA y passkeys).
 *
 * Estos paneles se renderizan dentro de la ficha de usuario (`oneUser/`) pero
 * viven en `components/security/`, así que su texto se quedó fuera del barrido
 * por carpetas hasta la Fase 1.11.b.
 */
export const SECURITY_MESSAGES = defineMessages({
  // --- Passkeys ---
  passkeysTitle: {id: 'admin.security.passkeys.title', defaultMessage: 'Passkeys'},
  passkeysIntro: {
    id: 'admin.security.passkeys.intro',
    defaultMessage:
      'Sign in with Face ID, Touch ID, Windows Hello, or your device PIN without typing your password.',
  },
  passkeysRegistered: {
    id: 'admin.security.passkeys.registeredCount',
    defaultMessage: '{count, plural, one {# registered} other {# registered}}',
  },
  passkeysNone: {id: 'admin.security.passkeys.none', defaultMessage: 'No passkeys'},
  passkeysAddTitle: {id: 'admin.security.passkeys.addTitle', defaultMessage: 'Add a passkey'},
  passkeysNamePlaceholder: {
    id: 'admin.security.passkeys.namePlaceholder',
    defaultMessage: 'Optional name, for example "Work laptop"',
  },
  passkeysAddButton: {id: 'admin.security.passkeys.addButton', defaultMessage: 'Add passkey'},
  passkeysAddHint: {
    id: 'admin.security.passkeys.addHint',
    defaultMessage:
      'You can register more than one passkey so you are not locked out if a device is lost.',
  },
  passkeysRegisteredTitle: {
    id: 'admin.security.passkeys.registeredTitle',
    defaultMessage: 'Registered passkeys',
  },
  passkeysEmptyOther: {
    id: 'admin.security.passkeys.emptyOther',
    defaultMessage: 'This user has no registered passkeys.',
  },
  passkeysEmptyOwn: {
    id: 'admin.security.passkeys.emptyOwn',
    defaultMessage: 'No passkeys have been registered for this account yet.',
  },
  passkeyUnnamed: {id: 'admin.security.passkeys.unnamed', defaultMessage: 'Unnamed passkey'},
  passkeyCreated: {id: 'admin.security.passkeys.created', defaultMessage: 'Created: {date}'},
  passkeyLastUsed: {id: 'admin.security.passkeys.lastUsed', defaultMessage: 'Last used: {date}'},
  passkeyNeverUsed: {id: 'admin.security.passkeys.neverUsed', defaultMessage: 'Never used'},
  passkeyTransports: {
    id: 'admin.security.passkeys.transports',
    defaultMessage: 'Transports: {list}',
  },
  passkeyRevoke: {id: 'admin.security.passkeys.revoke', defaultMessage: 'Revoke'},
  passkeyRemove: {id: 'admin.security.passkeys.remove', defaultMessage: 'Remove'},
  passkeysAdminNote: {
    id: 'admin.security.passkeys.adminNote',
    defaultMessage:
      'Administrators can revoke lost or compromised passkeys, but only the user can register a new passkey from their own profile.',
  },

  // Confirmación de borrado: frase completa por variante. El verbo y el
  // complemento cambian juntos, y en ES no se pueden pegar por separado.
  passkeyConfirmRevoke: {
    id: 'admin.security.passkeys.confirm.revoke',
    defaultMessage: 'Revoke passkey "{name}" from this user account?',
  },
  passkeyConfirmRemove: {
    id: 'admin.security.passkeys.confirm.remove',
    defaultMessage: 'Remove passkey "{name}" from this account?',
  },

  // --- Toasts de passkeys ---
  toastOnlyFromProfile: {
    id: 'admin.security.passkeys.toast.onlyFromProfile',
    defaultMessage: 'Passkey registration is only available from the user profile.',
  },
  toastCannotStart: {
    id: 'admin.security.passkeys.toast.cannotStart',
    defaultMessage: 'Unable to start passkey registration.',
  },
  toastCannotFinish: {
    id: 'admin.security.passkeys.toast.cannotFinish',
    defaultMessage: 'Unable to finish passkey registration.',
  },
  toastRegistered: {
    id: 'admin.security.passkeys.toast.registered',
    defaultMessage: 'Passkey registered successfully.',
  },
  toastCannotCreate: {
    id: 'admin.security.passkeys.toast.cannotCreate',
    defaultMessage: 'Unable to create the passkey.',
  },
  toastNoPermission: {
    id: 'admin.security.passkeys.toast.noPermission',
    defaultMessage: 'You do not have permission to manage passkeys for this account.',
  },
  toastUserNotFound: {
    id: 'admin.security.passkeys.toast.userNotFound',
    defaultMessage: 'User not found for passkey revocation.',
  },
  toastCannotRevoke: {
    id: 'admin.security.passkeys.toast.cannotRevoke',
    defaultMessage: 'Unable to revoke this passkey.',
  },
  toastRevoked: {
    id: 'admin.security.passkeys.toast.revoked',
    defaultMessage: 'Passkey revoked successfully.',
  },
  toastCannotRemove: {
    id: 'admin.security.passkeys.toast.cannotRemove',
    defaultMessage: 'Unable to remove this passkey.',
  },
  toastRemoved: {
    id: 'admin.security.passkeys.toast.removed',
    defaultMessage: 'Passkey removed successfully.',
  },

  // --- 2FA ---
  twoFaEnabled: {id: 'admin.security.twoFa.enabled', defaultMessage: 'Enabled'},
  twoFaDisabled: {id: 'admin.security.twoFa.disabled', defaultMessage: 'Disabled'},
  twoFaRequiredBadge: {id: 'admin.security.twoFa.requiredBadge', defaultMessage: 'Required'},
  twoFaFallbackOn: {
    id: 'admin.security.twoFa.fallbackOn',
    defaultMessage: 'Email fallback is enabled for login recovery.',
  },
  twoFaIntro: {
    id: 'admin.security.twoFa.intro',
    defaultMessage:
      'Primary method: authenticator app. Secondary method: email code during login if the app is unavailable.',
  },
  twoFaEnableButton: {id: 'admin.security.twoFa.enableButton', defaultMessage: 'Enable 2FA'},
  twoFaResetButton: {id: 'admin.security.twoFa.resetButton', defaultMessage: 'Reset 2FA'},
  twoFaRequirementTitle: {
    id: 'admin.security.twoFa.requirementTitle',
    defaultMessage: '2FA requirement',
  },
  twoFaRequirementIntro: {
    id: 'admin.security.twoFa.requirementIntro',
    defaultMessage: 'Require this user to configure and keep two-factor authentication enabled.',
  },
  twoFaRequireSetup: {id: 'admin.security.twoFa.requireSetup', defaultMessage: 'Require 2FA setup'},
  twoFaRequireHintOn: {
    id: 'admin.security.twoFa.requireHintOn',
    defaultMessage: 'If this remains enabled, the user will not be able to disable 2FA.',
  },
  twoFaRequireHintOff: {
    id: 'admin.security.twoFa.requireHintOff',
    defaultMessage:
      'If this is enabled, the user will be redirected to profile and must configure 2FA at next login.',
  },
  twoFaFallbackTitle: {
    id: 'admin.security.twoFa.fallbackTitle',
    defaultMessage: 'Email fallback for login recovery',
  },
  twoFaFallbackIntro: {
    id: 'admin.security.twoFa.fallbackIntro',
    defaultMessage:
      'Allow this user to receive a 6-digit code by email during login if they cannot access their authenticator app.',
  },
  twoFaAllowFallback: {
    id: 'admin.security.twoFa.allowFallback',
    defaultMessage: 'Allow email fallback',
  },
  twoFaFallbackHintOn: {
    id: 'admin.security.twoFa.fallbackHintOn',
    defaultMessage: 'This takes effect immediately on the next 2FA login challenge.',
  },
  twoFaFallbackHintOff: {
    id: 'admin.security.twoFa.fallbackHintOff',
    defaultMessage: 'This setting will be used when the user enables 2FA.',
  },
  twoFaRequiredWarning: {
    id: 'admin.security.twoFa.requiredWarning',
    defaultMessage:
      'Two-factor authentication is required for your account. Configure it before leaving this page.',
  },
  twoFaSetupTitle: {
    id: 'admin.security.twoFa.setupTitle',
    defaultMessage: 'Add this account to your authenticator app',
  },
  twoFaSetupIntro: {
    id: 'admin.security.twoFa.setupIntro',
    defaultMessage:
      'Scan the QR with Google Authenticator or Microsoft Authenticator. If you cannot scan it, use the setup code below and then enter the 6-digit code generated by the app.',
  },
  twoFaQrUnavailable: {
    id: 'admin.security.twoFa.qrUnavailable',
    defaultMessage: 'QR not available. Use the setup code below.',
  },
  twoFaScanHint: {
    id: 'admin.security.twoFa.scanHint',
    defaultMessage: 'Scan from your phone to add this account quickly.',
  },
  twoFaSetupCodeLabel: {
    id: 'admin.security.twoFa.setupCodeLabel',
    defaultMessage: 'Authenticator setup code',
  },
  twoFaSetupCodeHint: {
    id: 'admin.security.twoFa.setupCodeHint',
    defaultMessage: 'Use this code if the app asks for a key instead of scanning a QR.',
  },
  twoFaVerificationCode: {
    id: 'admin.security.twoFa.verificationCode',
    defaultMessage: '6-digit verification code',
  },
  twoFaAdvancedLink: {
    id: 'admin.security.twoFa.advancedLink',
    defaultMessage: 'Advanced setup link',
  },
  twoFaConfirmEnable: {
    id: 'admin.security.twoFa.confirmEnable',
    defaultMessage: 'Confirm and enable',
  },
  twoFaDisableTitle: {
    id: 'admin.security.twoFa.disableTitle',
    defaultMessage: 'Disable two-factor authentication',
  },
  twoFaDisableIntro: {
    id: 'admin.security.twoFa.disableIntro',
    defaultMessage:
      'Enter your current password and a valid code from your authenticator app to disable 2FA.',
  },
  twoFaCurrentPassword: {
    id: 'admin.security.twoFa.currentPassword',
    defaultMessage: 'Current password',
  },
  twoFaAuthenticatorCode: {
    id: 'admin.security.twoFa.authenticatorCode',
    defaultMessage: 'Authenticator code',
  },
  twoFaDisableButton: {id: 'admin.security.twoFa.disableButton', defaultMessage: 'Disable 2FA'},
  twoFaRequiredTitle: {
    id: 'admin.security.twoFa.requiredTitle',
    defaultMessage: 'Two-factor authentication is required',
  },
  twoFaRequiredIntro: {
    id: 'admin.security.twoFa.requiredIntro',
    defaultMessage: 'Your organization requires 2FA for this account, so it cannot be disabled.',
  },
  twoFaOtherUserNote: {
    id: 'admin.security.twoFa.otherUserNote',
    defaultMessage:
      'Each user must connect their own authenticator app from their profile. Administrators can reset 2FA and manage email fallback if the user loses access to their phone.',
  },

  // Etiquetas de lo que se copia. Descriptores para que la frase de abajo las
  // reciba ya traducidas.
  labelSetupCode: {id: 'admin.security.twoFa.label.setupCode', defaultMessage: 'setup code'},
  labelSetupLink: {id: 'admin.security.twoFa.label.setupLink', defaultMessage: 'setup link'},
  toastCopyManually: {
    id: 'admin.security.twoFa.toast.copyManually',
    defaultMessage: 'Copy the {label} manually.',
  },
  toastCopied: {id: 'admin.security.twoFa.toast.copied', defaultMessage: '{label} copied.'},
  toastCopyFailed: {
    id: 'admin.security.twoFa.toast.copyFailed',
    defaultMessage: 'Unable to copy the {label}.',
  },

  // --- Toasts de 2FA ---
  toastSetupStartFailed: {
    id: 'admin.security.twoFa.toast.setupStartFailed',
    defaultMessage: 'Unable to start two-factor setup.',
  },
  toastSetupStarted: {
    id: 'admin.security.twoFa.toast.setupStarted',
    defaultMessage: 'Two-factor setup started.',
  },
  toastCodeRequired: {
    id: 'admin.security.twoFa.toast.codeRequired',
    defaultMessage: 'Verification code is required.',
  },
  toastConfirmFailed: {
    id: 'admin.security.twoFa.toast.confirmFailed',
    defaultMessage: 'Unable to confirm two-factor setup.',
  },
  toastEnabled: {
    id: 'admin.security.twoFa.toast.enabled',
    defaultMessage: 'Two-factor authentication enabled.',
  },
  toastPasswordRequired: {
    id: 'admin.security.twoFa.toast.passwordRequired',
    defaultMessage: 'Current password is required.',
  },
  toastAuthCodeRequired: {
    id: 'admin.security.twoFa.toast.authCodeRequired',
    defaultMessage: 'Authenticator code is required.',
  },
  toastDisableFailed: {
    id: 'admin.security.twoFa.toast.disableFailed',
    defaultMessage: 'Unable to disable two-factor authentication.',
  },
  toastDisabled: {
    id: 'admin.security.twoFa.toast.disabled',
    defaultMessage: 'Two-factor authentication disabled.',
  },
  toastResetDone: {
    id: 'admin.security.twoFa.toast.resetDone',
    defaultMessage: 'Two-factor authentication was reset for this user.',
  },
  toastResetFailed: {
    id: 'admin.security.twoFa.toast.resetFailed',
    defaultMessage: 'Unable to reset two-factor authentication.',
  },
  toastFallbackEnabled: {
    id: 'admin.security.twoFa.toast.fallbackEnabled',
    defaultMessage: 'Email fallback enabled for this user.',
  },
  toastFallbackFailed: {
    id: 'admin.security.twoFa.toast.fallbackFailed',
    defaultMessage: 'Unable to update email fallback for this user.',
  },
  toastRequiredEnabled: {
    id: 'admin.security.twoFa.toast.requiredEnabled',
    defaultMessage: '2FA is now required for this user.',
  },
  toastFallbackDisabled: {
    id: 'admin.security.twoFa.toast.fallbackDisabled',
    defaultMessage: 'Email fallback disabled for this user.',
  },
  toastRequiredRemoved: {
    id: 'admin.security.twoFa.toast.requiredRemoved',
    defaultMessage: '2FA requirement removed.',
  },
  toastRequiredFailed: {
    id: 'admin.security.twoFa.toast.requiredFailed',
    defaultMessage: 'Unable to update the 2FA requirement for this user.',
  },
})
